/**
 * The legend chip's swatch, as a miniature of the stroke it names.
 *
 * `LegendChip` has always drawn the swatch from the line's own colour and from
 * WHETHER it is dashed (#2506: "the plot and the legend cannot disagree about
 * which stroke belongs to which name"). While the Kauf line was the only dashed
 * one, a boolean was an identity. HOF-214 puts two more dashed strokes on the
 * same plot — Benchmark Verbraucherebene `2 4` and Benchmark Handelsebene
 * `5 3 1 3` beside
 * Kauf's `7 3` — and three chips then drew one generic 5-3 swatch beside three
 * different lines, which is the #2506 disagreement in a new place.
 *
 * So the swatch takes the dasharray itself. Same numbers, same pixels, same
 * scale as the SVG stroke.
 */

/** The swatch's ink. Matches `.preisspiegel-chart__chip-sw`'s own fallback. */
const SWATCH_INK = 'var(--cc, #64748b)';

/**
 * Turn an SVG `stroke-dasharray` into the `background` a 0.9rem-wide swatch can
 * wear, or `null` when the string is not one — the caller then falls back to
 * the generic dashed gradient in the stylesheet. Never returns a malformed
 * gradient: CSS drops an invalid `background` wholesale, and an INVISIBLE
 * swatch is a worse legend than a generic one.
 */
export function dashSwatchGradient(dash: string): string | null {
  const runs = dash.trim().split(/[\s,]+/).filter(Boolean).map(Number);
  if (runs.length === 0) return null;
  if (runs.some((n) => !Number.isFinite(n) || n < 0)) return null;
  // SVG repeats an ODD-length dasharray twice before the pattern comes round,
  // so `1 2 3` paints on-1 off-2 on-3 off-1 on-2 off-3. A gradient with the
  // shorter period would be a different pattern from the line it names.
  const pattern = runs.length % 2 === 1 ? [...runs, ...runs] : runs;
  const period = pattern.reduce((a, b) => a + b, 0);
  if (period <= 0) return null;

  let at = 0;
  const stops = pattern.map((run, i) => {
    const from = at;
    at += run;
    return `${i % 2 === 0 ? SWATCH_INK : 'transparent'} ${from}px ${at}px`;
  });
  return `repeating-linear-gradient(to right, ${stops.join(', ')})`;
}
